
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const FatiguePage = () => {
  const [selectedSymptom, setSelectedSymptom] = useState('');

  const symptoms = [
    '朝起きるのがつらい',
    '日中の眠気',
    '集中力の低下',
    '体が重い',
    'やる気が出ない',
    '頭がぼーっとする'
  ];

  const causes = [
    {
      title: 'ストレス',
      description: '精神的・身体的ストレスによる疲労',
      icon: 'ri-emotion-unhappy-line'
    },
    {
      title: '睡眠不足',
      description: '質の悪い睡眠や睡眠時間の不足',
      icon: 'ri-moon-line'
    },
    {
      title: '栄養不足',
      description: 'ビタミン・ミネラルの不足',
      icon: 'ri-apple-line'
    },
    {
      title: '運動不足',
      description: '筋力低下や血行不良',
      icon: 'ri-run-line'
    }
  ];

  const treatments = [
    {
      category: '漢方薬',
      items: [
        { name: '補中益気湯', effect: '気力・体力の回復' },
        { name: '十全大補湯', effect: '全身の疲労回復' },
        { name: '朝鮮人参養栄湯', effect: '慢性疲労・貧血' }
      ]
    },
    {
      category: 'サプリメント',
      items: [
        { name: 'ビタミンB群', effect: 'エネルギー代謝の改善' },
        { name: 'コエンザイムQ10', effect: '細胞のエネルギー産生' },
        { name: '鉄分', effect: '貧血による疲労の改善' }
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "疲労・だるさの漢方治療 - 一心堂薬局",
    "description": "慢性疲労やだるさの症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な疲労回復を目指します。",
    "url": `${import.meta.env.VITE_SITE_URL}/fatigue`,
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "慢性疲労",
      "alternateName": "疲労・だるさ",
      "description": "持続的な疲労感やだるさが続く状態",
      "possibleCause": [
        "ストレス",
        "睡眠不足",
        "栄養不足",
        "運動不足"
      ],
      "signOrSymptom": [
        "朝起きるのがつらい",
        "日中の眠気",
        "集中力の低下",
        "体が重い",
        "やる気が出ない"
      ]
    },
    "about": {
      "@type": "MedicalBusiness",
      "name": "一心堂薬局",
      "telephone": "0479-70-6065"
    },
    "specialty": "漢方薬学",
    "medicalAudience": {
      "@type": "Patient"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <SEOHead
        title="疲労・だるさの漢方治療 - 一心堂薬局"
        description="慢性疲労やだるさの症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な疲労回復を目指します。"
        keywords="疲労,だるさ,慢性疲労,漢方治療,一心堂薬局,多古町,千葉県,体質改善,補中益気湯"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/fatigue`}
        ogTitle="疲労・だるさの漢方治療 - 一心堂薬局"
        ogDescription="慢性疲労やだるさの症状、原因、漢方での治療法について詳しく解説。体質改善による根本的な疲労回復を目指します。"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-arrow-left-line text-green-800 text-xl"></i>
              <span className="text-green-800 font-medium">戻る</span>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>一心堂薬局</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/752fc7fab98f55a75799270b3dae611d.jpg"
              alt="疲労・だるさのイメージ"
              className="w-full h-48 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-2xl font-bold text-blue-800 mb-2">
            疲労・だるさの<br />お悩み解決
          </h1>
          <p className="text-blue-600 text-lg mb-4">
            漢方薬で根本から改善
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            慢性的な疲労やだるさは体質改善で<br />
            根本的な解決を目指しましょう
          </p>
        </div>
      </div>

      {/* Symptoms Check */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-blue-800 mb-6 text-center">
            こんな症状はありませんか？
          </h2>
          <div className="space-y-3">
            {symptoms.map((symptom, index) => (
              <label key={index} className="flex items-center space-x-3 p-4 bg-blue-50 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors">
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-blue-600 rounded opacity-0 absolute"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedSymptom(symptom);
                    }
                  }}
                />
                <div className="w-5 h-5 border-2 border-blue-300 rounded flex items-center justify-center">
                  <i className="ri-check-line text-blue-600 text-sm opacity-0"></i>
                </div>
                <span className="text-gray-700">{symptom}</span>
              </label>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              3つ以上当てはまる場合は、専門的なケアをおすすめします
            </p>
          </div>
        </div>
      </div>

      {/* Causes Section */}
      <div className="px-4 py-8 bg-blue-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-blue-800 mb-6 text-center">
            疲労・だるさの主な原因
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {causes.map((cause, index) => (
              <div key={index} className="bg-white rounded-2xl p-4 text-center shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${cause.icon} text-blue-600 text-xl`}></i>
                </div>
                <h3 className="font-semibold text-blue-800 mb-2">{cause.title}</h3>
                <p className="text-xs text-gray-600">{cause.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Treatment Options */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-blue-800 mb-6 text-center">
            当薬局での治療法
          </h2>
          <div className="space-y-6">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-blue-50 rounded-2xl p-6">
                <h3 className="font-bold text-blue-800 mb-4 text-center">{treatment.category}</h3>
                <div className="space-y-3">
                  {treatment.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-white rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-800">{item.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.effect}</p>
                        </div>
                        <i className="ri-information-line text-blue-600 text-lg"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lifestyle Tips */}
      <div className="px-4 py-8 bg-blue-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-blue-800 mb-6 text-center">
            日常生活でできること
          </h2>
          <div className="bg-white rounded-2xl p-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-moon-line text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">質の良い睡眠</h4>
                  <p className="text-sm text-gray-600">7-8時間の規則正しい睡眠を心がけましょう</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-apple-line text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">バランスの良い食事</h4>
                  <p className="text-sm text-gray-600">ビタミンB群や鉄分を意識した食事を</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-walk-line text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">適度な運動</h4>
                  <p className="text-sm text-gray-600">軽いウォーキングから始めてみましょう</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consultation CTA */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-r from-blue-100 to-blue-50 rounded-2xl p-6 text-center">
            <h3 className="text-xl font-bold text-blue-800 mb-4">
              お一人で悩まずに<br />まずはご相談ください
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              体質や生活習慣に合わせて、最適な治療法をご提案いたします。<br />
              初回相談は無料です。
            </p>
            <div className="space-y-3">
              <button className="w-full bg-blue-600 text-white py-3 px-6 rounded-full font-medium">
                <i className="ri-phone-line mr-2"></i>
                電話で相談予約
              </button>
              <button className="w-full bg-white text-blue-600 py-3 px-6 rounded-full font-medium border border-blue-200">
                <i className="ri-chat-3-line mr-2"></i>
                LINEで相談
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-blue-600">
              <i className="ri-heart-pulse-fill text-lg"></i>
              <span className="text-xs mt-1">疲労相談</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default FatiguePage;
